/**
 * רווחיות — owner only, and the screen the Office plan is sold on.
 *
 * Worst first. The point of the report is the clients that cost more to service than they
 * pay, so sorting by margin ascending puts them at the top instead of making the owner
 * hunt for them. `ProfitabilityBar` reads out from a centred zero baseline, and negatives
 * are drawn in red ink with accounting parentheses — the two palette rules that make a
 * loss legible at a glance.
 */
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { formatMonthName } from "@onecpa/shared";
import {
  type Column,
  DataTable,
  EmptyState,
  ErrorState,
  KpiStrip,
  Money,
  PageHeader,
  ProfitabilityBar,
  StatusBadge,
} from "@onecpa/ui";
import { api } from "../api/client.js";
import { agorot, minutesToLabel, usePeriod, useRoleGate } from "../app/hooks.js";
import { useSession } from "../app/session.js";

interface Row {
  clientId: string;
  clientName: string;
  feeAmount: string | null;
  minutesLogged: number;
  laborCost: string | null;
  marginAmount: string | null;
  band: string;
}

export function Profitability() {
  const session = useSession();
  const { allowed } = useRoleGate(["owner"]);
  const { period, label, next, previous } = usePeriod();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["profitability", session.orgId, period.year, period.month],
    queryFn: () => api.analytics.profitability(session.orgId, period),
    enabled: allowed,
  });

  if (!allowed) {
    return (
      <ErrorState
        title="אין הרשאה למסך זה"
        message="דוח הרווחיות זמין לבעל המשרד בלבד, גם בשרת וגם במסד הנתונים."
      />
    );
  }

  if (error) {
    return <ErrorState message={(error as Error).message} onRetry={() => void refetch()} />;
  }

  const rows = data?.rows ?? [];
  const maxMagnitude = Math.max(
    ...rows
      .filter((row) => row.band !== "undefined_band")
      .map((row) => Math.abs(agorot(row.marginAmount))),
    1,
  );

  const columns: Column<Row>[] = [
    {
      key: "client",
      header: "לקוח",
      sticky: true,
      width: "220px",
      cell: (row) => (
        <Link
          to="/clients/$clientId"
          params={{ clientId: row.clientId }}
          className="link-underline text-sm font-semibold text-ink"
        >
          {row.clientName}
        </Link>
      ),
    },
    {
      key: "fee",
      header: "ריטיינר",
      align: "end",
      cell: (row) =>
        row.feeAmount ? (
          <Money agorot={agorot(row.feeAmount)} colorize={false} />
        ) : (
          <span className="text-xs text-ink-faint">לא הוגדר</span>
        ),
    },
    {
      key: "hours",
      header: "שעות",
      align: "end",
      cell: (row) => (
        <bdi className="tnum text-xs text-ink-muted">{minutesToLabel(row.minutesLogged)}</bdi>
      ),
    },
    {
      key: "cost",
      header: "עלות",
      align: "end",
      hideOnSmall: true,
      cell: (row) => <Money agorot={agorot(row.laborCost)} colorize={false} />,
    },
    {
      key: "margin",
      header: "רווח",
      cell: (row) =>
        // A client with no fee on record has no margin, only a cost. Drawing its
        // negative as a red loss bar would put red in front of the owner for a
        // data-entry gap, and red is reserved for money actually lost.
        row.band === "undefined_band" ? (
          <span className="text-xs text-ink-faint">אין ריטיינר להשוואה</span>
        ) : (
          <ProfitabilityBar
            marginAgorot={agorot(row.marginAmount)}
            maxAgorot={maxMagnitude}
            label={`רווח עבור ${row.clientName}`}
          />
        ),
    },
    {
      key: "band",
      header: "סיווג",
      align: "end",
      cell: (row) => <StatusBadge vocabulary="profitability" status={row.band} />,
    },
  ];

  const totals = data?.totals;

  return (
    <>
      <PageHeader
        title="רווחיות"
        subtitle="ריטיינר פחות שעות שנרשמו כפול עלות שעה"
        period={
          <span className="inline-flex items-center gap-1.5">
            <button type="button" onClick={previous} aria-label="חודש קודם" className="px-1">
              ‹
            </button>
            {label}
            <button type="button" onClick={next} aria-label="חודש הבא" className="px-1">
              ›
            </button>
          </span>
        }
      />

      <KpiStrip
        items={[
          {
            label: "הכנסות",
            value: totals ? <Money agorot={totals.feeAgorot} colorize={false} /> : undefined,
            tone: "ink",
          },
          {
            label: "עלות עבודה",
            value: totals ? <Money agorot={totals.laborAgorot} colorize={false} /> : undefined,
          },
          {
            label: "רווח",
            value: totals ? <Money agorot={totals.marginAgorot} /> : undefined,
            tone: (totals?.marginAgorot ?? 0) < 0 ? "red" : "ledger",
          },
          { label: "שעות", value: totals ? minutesToLabel(totals.minutes) : undefined },
          {
            label: "הפסדיים",
            value: data?.bands.loss,
            tone: (data?.bands.loss ?? 0) > 0 ? "red" : "ink",
          },
        ]}
      />

      <div className="p-5">
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(row) => row.clientId}
          loading={isLoading}
          caption={`רווחיות לפי לקוח, ${formatMonthName(period.month)} ${period.year}`}
          footnote="סוגריים = הפסד · הפס נקרא מקו האפס במרכז · לקוח בלי ריטיינר מסומן «לא הוגדר» ולא כהפסד"
          empty={
            <EmptyState
              title="אין נתוני רווחיות לחודש זה"
              description="הנתונים מחושבים על ידי העובד ברקע מתוך שעות שנרשמו וריטיינרים."
            />
          }
        />
      </div>
    </>
  );
}
